import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import SiteLayout from '../components/SiteLayout';
import { GhostButton, PrimaryButton, SectionIntro } from '../components/ui';
import { useLocale } from '../hooks/useLocale';
import screenLogin from '../assets/img/screens/login.png';
import screenProfile from '../assets/img/screens/profile.png';
import screenAdvisors from '../assets/img/screens/advisors.png';
import screenActivity from '../assets/img/screens/activity.png';

const url = process.env.REACT_APP_URL;

const SCREEN_IMAGES = {
  login: screenLogin,
  profile: screenProfile,
  advisors: screenAdvisors,
  activity: screenActivity,
};

function PhoneMockup({ screen, className = '' }) {
  const image = SCREEN_IMAGES[screen.id];

  return (
    <figure className={`phone-mockup ${className}`.trim()}>
      <div className="phone-mockup__frame">
        <div className="phone-mockup__notch" aria-hidden="true" />
        <img
          src={image}
          alt={screen.label}
          className="phone-mockup__screen"
          loading="lazy"
        />
      </div>
      <figcaption className="phone-mockup__caption">
        <span className="phone-mockup__label">{screen.label}</span>
        <span className="phone-mockup__text">{screen.caption}</span>
      </figcaption>
    </figure>
  );
}

function PhoneCluster({ screens }) {
  return (
    <div className="phone-cluster">
      {screens.map((screen, index) => (
        <PhoneMockup
          key={screen.id}
          screen={screen}
          className={`phone-cluster__item phone-cluster__item--${index + 1}`}
        />
      ))}
    </div>
  );
}

function ShowcaseHighlights({ items }) {
  return (
    <ul className="showcase-highlights">
      {items.map((item) => (
        <li key={item.title} className="showcase-highlight">
          <strong>{item.title}</strong>
          <p>{item.copy}</p>
        </li>
      ))}
    </ul>
  );
}

function HeroVisual({ copy }) {
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hero-panel hero-panel--main">
        <div className="hero-panel__header">
          <span className="status-dot" />
          <span>{copy.session}</span>
        </div>
        <div className="hero-chat">
          <div className="chat-bubble chat-bubble--incoming">{copy.incoming1}</div>
          <div className="chat-bubble chat-bubble--outgoing">{copy.outgoing1}</div>
          <div className="chat-bubble chat-bubble--incoming">{copy.incoming2}</div>
          <div className="chat-bubble chat-bubble--outgoing chat-bubble--accent">
            {copy.outgoing2}
          </div>
        </div>
        <div className="hero-metrics">
          <div>
            <strong>12</strong>
            <span>{copy.weeks}</span>
          </div>
          <div>
            <strong>4</strong>
            <span>{copy.areas}</span>
          </div>
          <div>
            <strong>1</strong>
            <span>{copy.route}</span>
          </div>
        </div>
      </div>
      <div className="hero-panel hero-panel--floating">
        <p>{copy.confidential}</p>
        <strong>{copy.protected}</strong>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { t } = useLocale();

  return (
    <SiteLayout t={t}>
      <main id="contenido">
        <section id="inicio" className="hero">
          <div className="shell hero__grid">
            <div className="hero__copy">
              <p className="eyebrow">{t.hero.eyebrow}</p>
              <h1>{t.hero.title}</h1>
              <p className="hero__lead">{t.hero.lead}</p>
              <div className="hero__actions">
                <PrimaryButton to="/#contacto">{t.hero.schedule}</PrimaryButton>
                <GhostButton to="/#servicios">{t.hero.services}</GhostButton>
              </div>
              <p className="hero__note">{t.hero.note}</p>
            </div>
            <HeroVisual copy={t.heroVisual} />
          </div>
        </section>

        <section className="statement">
          <div className="shell statement__inner">
            <div className="statement__chips" aria-hidden="true">
              {t.statement.chips.map((item) => (
                <span key={item} className="statement-chip">
                  {item}
                </span>
              ))}
            </div>
            <h2>{t.statement.title}</h2>
          </div>
        </section>

        <section id="servicios" className="feature feature--split">
          <div className="shell feature__grid">
            <div className="feature__visual feature__visual--cards">
              {t.services.highlights.map((item) => (
                <article key={item.title} className="service-card">
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
            <div className="feature__content">
              <SectionIntro
                eyebrow={t.services.eyebrow}
                title={t.services.title}
                description={t.services.description}
              />
              <a href="#contacto" className="text-link">
                {t.services.moreInfo}
              </a>
            </div>
          </div>
        </section>

        <section id="metodologia" className="feature feature--reverse feature--band">
          <div className="shell feature__grid">
            <div className="feature__content">
              <SectionIntro
                eyebrow={t.methodology.eyebrow}
                title={
                  <>
                    {t.methodology.titleLead}
                    <span className="accent-line">{t.methodology.titleAccent}</span>
                  </>
                }
                description={t.methodology.description}
              />
              <a href="#confidencialidad" className="text-link">
                {t.methodology.moreInfo}
              </a>
            </div>
            <div className="feature__visual feature__visual--timeline">
              {t.methodology.steps.map((step, index) => (
                <article key={step.label} className="timeline-step">
                  <span className="timeline-step__index">{index + 1}</span>
                  <div>
                    <h3>{step.label}</h3>
                    <p>{step.detail}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="confidencialidad" className="feature feature--split feature--showcase">
          <div className="shell feature__grid">
            <div className="feature__content">
              <SectionIntro
                eyebrow={t.confidentiality.eyebrow}
                title={t.confidentiality.title}
                description={t.confidentiality.description}
              />
              <ShowcaseHighlights items={t.confidentiality.highlights} />
            </div>
            <div className="feature__visual feature__visual--phones">
              <PhoneCluster screens={t.confidentiality.screens} />
            </div>
          </div>
        </section>

        <section id="casos" className="feature feature--reverse feature--showcase">
          <div className="shell feature__grid">
            <div className="feature__visual feature__visual--phones">
              <PhoneCluster screens={t.cases.screens} />
            </div>
            <div className="feature__content">
              <SectionIntro
                eyebrow={t.cases.eyebrow}
                title={
                  <>
                    {t.cases.titleLead}
                    <span className="accent-line">{t.cases.titleAccent}</span>
                  </>
                }
                description={t.cases.description}
              />
              <ShowcaseHighlights items={t.cases.highlights} />
            </div>
          </div>
        </section>

        <section id="empresas" className="cta-band">
          <div className="shell cta-band__inner">
            <div>
              <p className="eyebrow">{t.companies.eyebrow}</p>
              <h2>{t.companies.title}</h2>
              <p className="section-copy">{t.companies.description}</p>
              <Link to={`${url}/adviser`} target="_blank" className="btn btn-primary">
                {t.companies.cta}
              </Link>
            </div>
            <div className="cta-band__panel" aria-hidden="true">
              {t.companies.stats.map((stat) => (
                <div key={stat.label} className="cta-stat">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contacto" className="contact">
          <div className="shell contact__inner">
            <SectionIntro
              eyebrow={t.contact.eyebrow}
              title={t.contact.title}
              description={t.contact.description}
              align="center"
            />
            <ContactForm />
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}
